import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import DraggableCollapseContainer from '@/components/DraggableCollapseContainer';
import { useController } from 'react-hook-form';
import { Draggable } from '@/declarations/Draggable';
import Container from '@/components/Container';
import TextInput from '@/components/forms/TextInput';
import Styles from '@/assets/js/Styles';
import DeleteButton from '@/components/DeleteButton';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { ReferencesBlockModel, ReferenceType, ReferenceItemModel } from '@/declarations/models/blocks/ReferencesBlock';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';

type ReferenceItemProps = {
  item: ReferenceItemModel;
  itemPathPrefix: string;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRemove: (index: number) => void;
  index: number;
  type: Draggable;
  initiallyOpen?: boolean;
};

const ReferenceItem: React.FC<ReferenceItemProps> = ({
  item,
  itemPathPrefix,
  onReorder,
  onRemove,
  index,
  type,
  initiallyOpen = false,
}) => {
  const { t: tComponents } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');

  const {
    field: { value: referenceType, onChange: onChangeReferenceType },
  } = useController<BlockSpecificPage<ReferencesBlockModel>, keyof BlockSpecificPage<ReferencesBlockModel>>({
    name: `${itemPathPrefix}.referenceType` as keyof BlockSpecificPage<ReferencesBlockModel>,
  });

  const headerContent = () => {
    const parts: string[] = [];
    parts.push(`${tComponents('ReferencesBlock.referenceLabel')}: `);
    if (item.referenceType) {
      parts.push(tComponents(`ReferencesBlock.referenceTypeOptions.${item.referenceType}`));
    }
    if (item.author) {
      const author = item.author.length > 40 ? `${item.author.substring(0, 40)}...` : item.author;
      parts.push(`- ${author}`);
    }
    if (item.title) {
      const title = item.title.length > 20 ? `${item.title.substring(0, 20)}...` : item.title;
      parts.push(`(${title})`);
    }
    return (
      <Typography
        sx={{ textAlign: 'left', textDecoration: 'underline', fontSize: '0.9rem', fontWeight: 'bold', ml: 2 }}>
        {parts.join(' ')}
      </Typography>
    );
  };

  return (
    <DraggableCollapseContainer
      type={type}
      index={index}
      onReorder={onReorder}
      headerContent={headerContent}
      color={Styles.Colors.LIGHT_GREY}
      secondaryAction={
        <Container right>
          <DeleteButton
            onConfirm={() => onRemove(index)}
            component='IconButton'
            tooltip={tCommon('remove')}
            deleteIcon={<MaterialSymbol name='do_not_disturb_on' />}
          />
        </Container>
      }
      initiallyOpen={initiallyOpen}>
      <Container top left fullWidth column p={1}>
        <Container left fullWidth mt={-3} p={1}>
          <FormControl>
            <h3 className='block-sub-label'> {tComponents('ReferencesBlock.referenceType')}:</h3>
            <RadioGroup row value={referenceType} onChange={(e) => onChangeReferenceType(e.target.value)}>
              {Object.values(ReferenceType).map((rtype) => (
                <FormControlLabel
                  key={rtype}
                  value={rtype}
                  control={<Radio />}
                  label={tComponents(`ReferencesBlock.referenceTypeOptions.${rtype}`)}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Container>

        <Container left fullWidth p={1} column gap={3}>
          <Container left fullWidth p={0} gap={1}>
            <div style={{ width: '150px' }}>
              <TextInput
                label={tComponents('ReferencesBlock.referenceNumber')}
                variant='outlined'
                path={`${itemPathPrefix}.referenceNumber`}
              />
            </div>
            <TextInput
              label={tComponents('ReferencesBlock.author')}
              variant='outlined'
              path={`${itemPathPrefix}.author`}
            />
          </Container>

          <TextInput
            label={tComponents('ReferencesBlock.title')}
            variant='outlined'
            defaultValue=''
            multiline
            path={`${itemPathPrefix}.title`}
          />

          {(item.referenceType === ReferenceType.ARTICLE ||
            item.referenceType === ReferenceType.NEWSPAPER ||
            item.referenceType === ReferenceType.REPORT ||
            item.referenceType === ReferenceType.WEBSITE ||
            item.referenceType === ReferenceType.OTHER) && (
            <Container left fullWidth>
              <TextInput
                label={tComponents('ReferencesBlock.media')}
                variant='outlined'
                defaultValue=''
                path={`${itemPathPrefix}.media`}
              />
            </Container>
          )}

          <Container left fullWidth>
            {(item.referenceType === ReferenceType.BOOK ||
              item.referenceType === ReferenceType.ARTICLE ||
              item.referenceType === ReferenceType.NEWSPAPER ||
              item.referenceType === ReferenceType.DOCUMENT ||
              item.referenceType === ReferenceType.REPORT) && (
              <TextInput
                label={tComponents('ReferencesBlock.issue')}
                variant='outlined'
                defaultValue=''
                path={`${itemPathPrefix}.issue`}
              />
            )}
            {(item.referenceType === ReferenceType.ARTICLE ||
              item.referenceType === ReferenceType.NEWSPAPER ||
              item.referenceType === ReferenceType.REPORT) && (
              <TextInput
                label={tComponents('ReferencesBlock.pageNumber')}
                variant='outlined'
                defaultValue=''
                path={`${itemPathPrefix}.page`}
              />
            )}
          </Container>

          <Container left fullWidth>
            {(item.referenceType === ReferenceType.BOOK ||
              item.referenceType === ReferenceType.DOCUMENT ||
              item.referenceType === ReferenceType.REPORT ||
              item.referenceType === ReferenceType.OTHER) && (
              <TextInput
                label={tComponents('ReferencesBlock.place')}
                variant='outlined'
                defaultValue=''
                path={`${itemPathPrefix}.place`}
              />
            )}
            {item.referenceType === ReferenceType.REPORT && (
              <TextInput
                label={tComponents('ReferencesBlock.reportNumber')}
                variant='outlined'
                defaultValue=''
                path={`${itemPathPrefix}.reportNumber`}
              />
            )}

            {(item.referenceType === ReferenceType.BOOK ||
              item.referenceType === ReferenceType.DOCUMENT ||
              item.referenceType === ReferenceType.OTHER) && (
              <TextInput
                label={tComponents('ReferencesBlock.publisher')}
                variant='outlined'
                defaultValue=''
                path={`${itemPathPrefix}.publisher`}
              />
            )}

            <TextInput
              label={tComponents('ReferencesBlock.date')}
              variant='outlined'
              defaultValue=''
              path={`${itemPathPrefix}.date`}
            />
          </Container>
          {item.referenceType === ReferenceType.ARTICLE && (
            <TextInput
              label={tComponents('ReferencesBlock.doi')}
              variant='outlined'
              defaultValue=''
              path={`${itemPathPrefix}.doi`}
            />
          )}
          {(item.referenceType === ReferenceType.WEBSITE ||
            item.referenceType === ReferenceType.ARTICLE ||
            item.referenceType === ReferenceType.DOCUMENT) && (
            <TextInput
              label={tComponents('ReferencesBlock.url')}
              variant='outlined'
              defaultValue=''
              path={`${itemPathPrefix}.url`}
            />
          )}
        </Container>
      </Container>
    </DraggableCollapseContainer>
  );
};

export default ReferenceItem;
